import { FormControl, FormLabel } from '@chakra-ui/react';
import {
  useAutoUpdateForm,
  AutoUpdateWrapper,
} from '@texas/components/shared/autoUpdate/useAutoUpdateForm';
import {
  compositionGroupApi,
  AutoUpdateCompositionGroupConfig,
  CompositionGroupConfig,
  EMPTY_AUTO_UPDATE_COMPOSITION_GROUP_CONFIG,
} from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { productGroupsApi } from '@texas/api/endpoints/metadata/productGroupsApi';
import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { Position } from '@texas/components/shared/autoUpdate/types';
import { useTranslation } from 'react-i18next';

interface TemplateConfigProps {
  compositionGroupId: number;
}

export function TemplateConfig({ compositionGroupId }: TemplateConfigProps) {
  const { t } = useTranslation();
  const {
    data: productGroups,
    refetch: refetchProductGroups,
    loading: productGroupsLoading,
  } = useApiResource(productGroupsApi.getProductGroups);

  const {
    data: config,
    refetch: refetchConfig,
    loading: configLoading,
  } = useApiResource(compositionGroupApi.getConfig);

  useEffect(() => {
    refetchProductGroups();
    refetchConfig(compositionGroupId);
  }, [refetchProductGroups, refetchConfig, compositionGroupId]);

  const { registerAutoUpdate, form, control, triggerSubmit, resetValue } =
    useAutoUpdateForm<AutoUpdateCompositionGroupConfig, CompositionGroupConfig>(
      {
        defaultValue: {
          templateProductGroupId: config?.templateProductGroupId ?? null,
        },
        requestParams: [compositionGroupId],
        namePrefix: `compositionGroupConfig-${compositionGroupId}`,
        autoUpdateRequest: compositionGroupApi.autoUpdateConfig,
        fallbackMapObject: EMPTY_AUTO_UPDATE_COMPOSITION_GROUP_CONFIG,
      },
    );

  useEffect(() => {
    if (config) {
      resetValue('templateProductGroupId', config.templateProductGroupId);
    }
  }, [config, resetValue]);

  if (configLoading || !config) {
    return <SpinnerWithLabel label={t('general.loading')} />;
  }

  return (
    <form {...form}>
      <FormControl>
        <FormLabel>{t('configuration.templateProductGroup')}</FormLabel>
        <AutoUpdateWrapper
          autoUpdateProps={registerAutoUpdate}
          path="templateProductGroupId"
          position={Position.Bottom}
        >
          <Controller
            control={control}
            name="templateProductGroupId"
            render={({ field }) => (
              <TexasSelect
                value={
                  field.value
                    ? {
                        label:
                          productGroups?.find((pg) => pg.id === field.value)
                            ?.value ?? '',
                        value: field.value,
                      }
                    : null
                }
                onBlur={triggerSubmit}
                isLoading={productGroupsLoading}
                menuPortalTarget={document.body}
                isClearable={true}
                onChange={(e) => field.onChange(e?.value ?? null)}
                options={productGroups?.map((pg) => ({
                  label: `${pg.value} (${pg.code})`,
                  value: pg.id,
                }))}
              />
            )}
          />
        </AutoUpdateWrapper>
      </FormControl>
    </form>
  );
}
